import { styled } from '@linaria/react';
import {
  component,
  primitives,
  radius,
  semantic,
  size,
  space,
  textStyle,
} from '../../theme/tokens';

/*
 * inline-flex, because the design centres the control from the column around it. flex-wrap is a
 * backstop only: the narrow form below keeps the run inside a phone, so nothing should reach a
 * second row at any width the guide uses.
 */
export const PaginationNav = styled.nav`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${space['space-400']};

  /*
   * The narrow form carries up to seven slots — two jumps, two chevrons and three numbers — which
   * is 280px of circles before any gap. Measured against the 312px a 360px phone leaves inside
   * the guide's 24px insets: 16px gaps give 360px and 8px give 328px, both of which wrap to a
   * second row. 4px gives 304px and fits with room to spare.
   */
  @media screen and (max-width: 600px) {
    gap: ${space['space-100']};
  }
`;

export const PageRun = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${space['space-200']};

  /* Closes with the nav, so the seven-slot form reads as one group rather than three. */
  @media screen and (max-width: 600px) {
    gap: ${space['space-100']};
  }
`;

/**
 * Every slot — a page, a chevron, the ellipsis — is the same 40px circle, which is what keeps the
 * run from shifting as the numbers change width.
 *
 * Declared as an anchor so `href` types cleanly, and rendered as a button or a span through `as`.
 * The held state keys off `aria-current` rather than a style prop, so what is drawn cannot drift
 * from what is announced.
 *
 * `disabled` is declared because `as` does not widen the prop type: the anchor's attributes are all
 * TypeScript sees, so a button-only attribute has to be named here or it fails to compile.
 */
export const Slot = styled.a<{ disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: ${primitives.size['size-1000']};
  height: ${primitives.size['size-1000']};
  padding: 0;
  border: none;
  border-radius: ${radius('button', 'radius-full')};
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s ease;
  font-family: ${textStyle.body.md.strong.fontFamily};
  font-size: ${textStyle.body.md.strong.fontSize};
  font-weight: ${textStyle.body.md.strong.fontWeight};
  line-height: ${textStyle.body.md.strong.lineHeight};
  letter-spacing: ${textStyle.body.md.strong.letterSpacing};
  /*
   * No fill at rest: the design draws a resting slot in Style=Transparent, and the mock shows bare
   * numbers with only the current page filled. Deliberately NOT
   * component.button.transparent.enabled.bg, which resolves to a 5% wash — a token family named
   * transparent that paints one. The icon-ghost variant of Button hardcodes transparent for the
   * same reason.
   */
  background: transparent;
  color: ${component.button.transparent.enabled.text};

  /*
   * The glyph takes the icon token, not the text one. Both resolve to the same near-black in every
   * theme shipped so far, but they are separate custom properties and the design binds the icon
   * one — so a theme that moved them apart would otherwise drift here silently.
   */
  &[data-step] {
    color: ${component.button.transparent.enabled.icon};
  }

  &:hover {
    background: ${component.button.neutral.hover.bg};
  }

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
  }

  &[aria-current='page'] {
    background: ${component.button.neutral.pressed.bg};
    color: ${component.button.neutral.pressed.text};

    /* Held, so a hover must not lift the fill back off the page you are already on. */
    &:hover {
      background: ${component.button.neutral.pressed.bg};
    }
  }

  /*
   * Ahead of the block below, whose nested :hover would otherwise outrank this one. Dimmed by
   * opacity rather than the disabled ink, which is 10% black — legible on a filled slot, close to
   * invisible on an unfilled one. The base :disabled rule in StyledButton does the same.
   */
  &:disabled {
    opacity: 0.5;
  }

  &[data-decorative],
  &:disabled {
    cursor: default;

    &:hover {
      background: transparent;
    }
  }

  /*
   * The narrow form. Measured at 14px/Medium, the wide run is 392px at page 1 of 15 and 488px at
   * page 8 where both ellipses show, against 312px usable on a 360px phone — so every range
   * overflows a small phone and the widest overflows a 414px one. What goes is the first and last
   * anchors and the ellipses standing in for them, leaving the window: three numbers between the
   * two chevrons. Those slots are a subset of the wide run, so this hides slots rather than
   * rendering a second set of links, and every page stays in the markup for a crawler.
   *
   * 600px is the switch the rest of the library uses (Calendar, CalendarDay, JourneyPicker), not
   * the unused 576px breakpoints token.
   */
  /* Only the narrow form jumps to an end; the wide run already lists the first and last page. */
  &[data-jump] {
    display: none;
  }

  @media screen and (max-width: 600px) {
    &[data-wide] {
      display: none;
    }

    &[data-jump] {
      display: inline-flex;
    }

    /* The chevrons carry the paging here, so they take the fill the numbers do not. */
    &[data-step] {
      background: ${component.button.neutral.enabled.bg};
    }
  }
`;
